/**
 * UpgradePrompt
 * ──────────────────────────────────────────────────────────────
 * What a student sees when they hit a wall: the daily question cap, the
 * Tutor conversation cap, a locked 12th-grade subject, or a paid-only
 * feature. It is the highest-traffic paid surface in the app.
 *
 * Two rules, both learned from what this component used to do:
 *
 *   1. IT NEVER NAMES A TIER. It sold "Plano Essencial" long after the
 *      catalogue moved to Estudante and Premium, because the names were
 *      hardcoded here. Which tier unlocks which feature is per-feature admin
 *      config, so any tier named in this file is a guess that goes stale in
 *      silence. It shows a price, from the same endpoint the pricing page
 *      uses, and sends the student there to choose.
 *   2. IT NEVER PROMISES ANYTHING IT CANNOT KEEP. It used to collect an
 *      email for a launch waitlist; POST /waitlist pushes to an in-memory
 *      array that is lost on the next restart, so "avisamos-te" was never
 *      going to happen. Removed rather than reworded.
 *
 * With PAID_TIERS_ENABLED off the countdown still renders, because a student
 * on the free plan genuinely needs to know when their questions come back.
 * The sales half simply does not appear.
 */

import React, { useEffect, useState } from 'react'
import { getOffer, cheapestMonthlyMzn, formatMzn, openPricing, PAID_TIERS_ENABLED } from '../lib/payments'

export type UpgradeReason =
  | 'daily_limit'
  | 'tutor_daily_limit'
  | 'subjects_limit'
  | 'subscription_required'
  | 'exam_mode'

interface Props {
  reason?: UpgradeReason
  /** ISO string. Shown as a countdown for the two daily caps. */
  resetsAt?: string
  /**
   * Extra line under the body, for a wall that also has a FREE way out.
   * The subject-swap cooldown is the case this exists for: the student can
   * pay now, or wait until the swap date and change subjects for nothing.
   * Hiding the free option to sell harder would be dishonest, and most of
   * these students cannot pay at all.
   */
  detail?: React.ReactNode
  /**
   * Render for a modal card instead of a full page.
   *
   * The default layout claims min-h-[60vh] and its own padding because on
   * QuestionPage it IS the page, sitting on the page background. Dropped into
   * a modal that produced a card with no surface of its own: the text landed
   * straight on top of the subject grid behind it.
   */
  compact?: boolean
  onDismiss?: () => void
}

/**
 * Copy per wall. Numbers are deliberately absent: "as tuas 20 questões" was
 * hardcoded while the real figure comes from the tier's `questions_per_day`,
 * so an admin changing the cap silently made this text a lie.
 */
const MESSAGES: Record<UpgradeReason, { title: string; body: React.ReactNode; icon: string }> = {
  daily_limit: {
    icon:  '⏳',
    title: 'Limite diário atingido',
    body:  <>Já respondeste a todas as tuas questões de hoje. Activa um dos planos pagos para continuares a estudar e aumentares as chances de seres <strong>Admitido</strong>!</>,
  },
  tutor_daily_limit: {
    icon:  '💬',
    title: 'Limite de conversas atingido',
    body:  <>Já usaste as tuas conversas com o Tutor de hoje. Activa um dos planos pagos para continuares a estudar e aumentares as chances de seres <strong>Admitido</strong>!</>,
  },
  subjects_limit: {
    icon:  '📚',
    title: 'Disciplina bloqueada',
    body:  'Activa um dos planos pagos para estudares todas as disciplinas da 12ª classe!',
  },
  subscription_required: {
    icon:  '🔒',
    title: 'Funcionalidade bloqueada',
    body:  'Esta funcionalidade está disponível nos planos pagos.',
  },
  exam_mode: {
    icon:  '📝',
    title: 'Exame de Simulação',
    body:  'O Exame de Simulação está disponível nos planos pagos. Pratica em modo cronometrado e entra no ranking.',
  },
}

function timeUntil(isoString: string): string {
  const diff = new Date(isoString).getTime() - Date.now()
  if (diff <= 0) return '0h 0m'
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export const UpgradePrompt: React.FC<Props> = ({ reason = 'subscription_required', resetsAt, detail, compact = false, onDismiss }) => {
  const [fromMzn, setFromMzn] = useState<number | null>(null)

  const msg = MESSAGES[reason] ?? MESSAGES.subscription_required

  useEffect(() => {
    if (!PAID_TIERS_ENABLED) return
    let cancelled = false
    ;(async () => {
      try {
        const offer = await getOffer()
        if (!cancelled) setFromMzn(cheapestMonthlyMzn(offer))
      } catch {
        // Offline, or the offer call failed. The CTA still works: the pricing
        // page is the thing that has to load, not this hook. Showing the
        // button without a price beats hiding the only way to pay.
      }
    })()
    return () => { cancelled = true }
  }, [])

  const showsCountdown = (reason === 'daily_limit' || reason === 'tutor_daily_limit') && !!resetsAt

  return (
    <div className={`flex flex-col items-center justify-center text-center ${
      compact ? 'py-2' : 'min-h-[60vh] px-6'
    }`}>

      <div className={`rounded-xl bg-indigo-50 flex items-center justify-center ${compact ? 'w-12 h-12 mb-3' : 'w-16 h-16 mb-5'}`}>
        <span className={compact ? 'text-2xl' : 'text-3xl'} aria-hidden>{msg.icon}</span>
      </div>

      <h2 className={`font-semibold text-gray-900 mb-2 ${compact ? 'text-lg' : 'text-xl'}`}>{msg.title}</h2>
      <p className="text-sm text-gray-500 max-w-xs leading-relaxed">{msg.body}</p>
      {detail && (
        <p className="mt-2 text-xs text-gray-400 max-w-xs leading-relaxed">{detail}</p>
      )}

      {showsCountdown && (
        <div className="mt-4 bg-gray-50 border border-gray-100 rounded-xl px-5 py-3">
          <p className="text-xs text-gray-400">Volta em</p>
          <p className="text-2xl font-black text-gray-700 mt-0.5">{timeUntil(resetsAt!)}</p>
        </div>
      )}

      {PAID_TIERS_ENABLED && (
        <div className={`w-full max-w-sm ${compact ? 'mt-4' : 'mt-6'}`}>
          <button
            onClick={() => openPricing()}
            className="w-full py-3 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Ver planos
          </button>
          {fromMzn !== null && (
            <p className="text-xs text-gray-400 mt-2">
              A partir de {formatMzn(fromMzn)} por mês. Pagas por M-Pesa ou e-Mola.
            </p>
          )}
        </div>
      )}

      {onDismiss && (
        <button
          onClick={onDismiss}
          className="mt-4 text-sm text-gray-400 hover:text-gray-600"
        >
          Voltar
        </button>
      )}
    </div>
  )
}

export default UpgradePrompt
